import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { auth, db } from '@/config/firebase';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // 🔹 State
      user: null,
      userRole: null,
      userPermissions: [],
      isLoading: true,
      error: null,
      profile: null,
      preferences: {
        theme: 'light',
        notifications: true,
        language: 'en',
      },
      sessionTimeout: 30 * 60 * 1000, // 30 minutes
      lastActivity: Date.now(),
      publicRoutes: ['/public/tickets'],

      // 🔹 Authentication Actions
      login: async (email, password) => {
        try {
          set({ isLoading: true, error: null });

          // Sign in
          const result = await signInWithEmailAndPassword(auth, email, password);
          const userDoc = await getDoc(doc(db, 'users', result.user.uid));

          if (!userDoc.exists()) throw new Error('User data not found');

          const userData = userDoc.data();

          // Update store
          set({
            user: result.user,
            userRole: userData.role,
            profile: userData,
            isLoading: false,
            lastActivity: Date.now(),
          });

          return { user: result.user, userData };
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await signOut(auth);
          sessionStorage.clear(); // ✅ Ensure storage is cleared
          set({
            user: null,
            userRole: null,
            userPermissions: [],
            profile: null,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      // 🔹 Public Route Checker
      isPublicRoute: (pathname) => get().publicRoutes.some((route) => pathname.startsWith(route)),

      // 🔹 State Mutations
      setUser: (user) => set({ user, isLoading: false, lastActivity: Date.now() }),
      setUserRole: (role) => set({ userRole: role, lastActivity: Date.now() }),
      setUserPermissions: (permissions) =>
        set({ userPermissions: permissions, lastActivity: Date.now() }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      // 🔹 User & Session Management
      clearUser: () =>
        set({
          user: null,
          userRole: null,
          userPermissions: [],
          profile: null,
          isLoading: false,
          error: null,
        }),

      updateLastActivity: () => set({ lastActivity: Date.now() }),

      checkSessionTimeout: () => {
        const state = get();
        return state.user && Date.now() - state.lastActivity > state.sessionTimeout;
      },

      resetStore: () =>
        set({
          user: null,
          userRole: null,
          userPermissions: [],
          isLoading: false,
          error: null,
          profile: null,
          lastActivity: Date.now(),
        }),
    }),
    {
      name: 'auth-storage',
      storage: sessionStorage, // ✅ Simplified from `createJSONStorage`
      partialize: (state) => ({
        user: state.user,
        userRole: state.userRole,
        userPermissions: state.userPermissions,
        preferences: state.preferences,
      }),
    }
  )
);

export default useAuthStore;
